// AB3 Academy — sandbox session React context.
// Сам стейт живёт в sessionStorage (utils/sandboxSession.ts), provider синхронизирует
// React state с storage через CustomEvent, чтобы fetchWithAuth и UI видели одно и то же.
//
// Decision /plan-eng-review 1A: Context API + sessionStorage, no zustand.
// Storage scope: tab (handoff §7.2 — cross-tab может вступить в конфликт).
//
// Wire-up Banner↔Runner (см. мокап s34): Provider слушает SANDBOX_VERIFY_EVENT
// глобально и выполняет finish-practice + navigate. Banner живёт в Layout (нет
// доступа к eid/mid из URL); PracticeRunner unmount'ится при navigate('/tickets'),
// его listener бесполезен. Provider — единственное стабильное место.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SANDBOX_EVENT_EXPIRED,
  SANDBOX_EVENT_FORGED,
  SANDBOX_EVENT_INVALID,
  SANDBOX_EVENT_UPDATED,
  clearSession,
  isSandboxExpired,
  readStoredSession,
  remainingMs,
  setSession,
  type SandboxClearReason,
} from '../utils/sandboxSession';
import { learningService } from '../services/learning.service';
import { showToast } from '../utils/toast';
import { SANDBOX_VERIFY_EVENT } from '../utils/sandboxEvents';
import type { SandboxType } from '../types/sandbox.types';

export interface SandboxSessionState {
  /** base64-JSON header value; null когда нет активной practice */
  header: string | null;
  /** имя PG-схемы (для отображения в банере и в logger'е) */
  schemaName: string | null;
  /** ISO 8601 expiry */
  expiresAt: string | null;
  /** тип сессии */
  type: SandboxType | null;
  /** Wire-up context для finish-practice flow (см. handler ниже) */
  enrollmentId: number | null;
  moduleId: number | null;
  courseId: number | null;
}

export interface SandboxSessionContextValue extends SandboxSessionState {
  /**
   * Создать сессию (после успешного start-practice или recovery через /practice-header).
   * `enrollmentId/moduleId/courseId` ОБЯЗАТЕЛЬНЫ для wire-up с Banner — без них
   * глобальный listener не сможет дёрнуть finish-practice по клику «Проверить».
   * (Для recovery flow эти ID известны из URL params — useSandboxRecovery их прокидывает.)
   */
  start: (args: {
    header: string;
    schemaName: string;
    expiresAt: string;
    type?: SandboxType;
    enrollmentId?: number;
    moduleId?: number;
    courseId?: number;
  }) => void;
  /** Завершить сессию (перед finish-practice, или при 410/403/400 sandbox-related) */
  finish: (reason?: SandboxClearReason) => void;
  /** Активна ли сессия и не истекла ли */
  isActive: () => boolean;
  /** Сколько ms осталось (для countdown компонента) */
  getRemainingMs: () => number;
}

const Ctx = createContext<SandboxSessionContextValue | null>(null);

function readState(): SandboxSessionState {
  const stored = readStoredSession();
  const empty: SandboxSessionState = {
    header: null,
    schemaName: null,
    expiresAt: null,
    type: null,
    enrollmentId: null,
    moduleId: null,
    courseId: null,
  };
  if (!stored) return empty;
  if (isSandboxExpired(stored.expiresAt)) return empty;
  return {
    header: stored.header,
    schemaName: stored.schemaName,
    expiresAt: stored.expiresAt,
    type: stored.type,
    enrollmentId: stored.enrollmentId ?? null,
    moduleId: stored.moduleId ?? null,
    courseId: stored.courseId ?? null,
  };
}

export const SandboxSessionProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [state, setState] = useState<SandboxSessionState>(readState);
  const navigate = useNavigate();

  // Подписываемся на 4 события storage→state синхронизации.
  // Источники: fetchWithAuth interceptor (410/403/400 sandbox-related), компоненты.
  // Гард simplify PR #226: пропускаем setState если содержимое не изменилось —
  // 4 события могут срабатывать кластерами при ошибочных ответах.
  useEffect(() => {
    const refresh = () => {
      setState((prev) => {
        const next = readState();
        if (
          prev.header === next.header &&
          prev.schemaName === next.schemaName &&
          prev.expiresAt === next.expiresAt &&
          prev.type === next.type &&
          prev.enrollmentId === next.enrollmentId &&
          prev.moduleId === next.moduleId &&
          prev.courseId === next.courseId
        ) {
          return prev; // same reference — React не вызовет re-render
        }
        return next;
      });
    };
    window.addEventListener(SANDBOX_EVENT_UPDATED, refresh);
    window.addEventListener(SANDBOX_EVENT_EXPIRED, refresh);
    window.addEventListener(SANDBOX_EVENT_INVALID, refresh);
    window.addEventListener(SANDBOX_EVENT_FORGED, refresh);
    return () => {
      window.removeEventListener(SANDBOX_EVENT_UPDATED, refresh);
      window.removeEventListener(SANDBOX_EVENT_EXPIRED, refresh);
      window.removeEventListener(SANDBOX_EVENT_INVALID, refresh);
      window.removeEventListener(SANDBOX_EVENT_FORGED, refresh);
    };
  }, []);

  // Wire-up Banner→Runner: глобальный listener finish-practice flow.
  // Поток (см. мокап s34): user → Banner.dispatchEvent → window → handler →
  // 1) finish() — clear sandbox header ДО запроса (handoff §4.4 phase 3),
  //    иначе finish-practice пишет в sandbox.learning_progress (нет такой).
  // 2) learningService.finishPractice() — backend verify + cleanup.
  // 3) navigate to result page с passed=true|false через query.
  // На 410 (TTL истёк перед finish) — backend сам отвечает 410, interceptor
  // дёрнет clearSession('expired'), toast + ничего больше делать не нужно.
  useEffect(() => {
    const handleVerify = async () => {
      const current = readStoredSession();
      if (!current || !current.enrollmentId || !current.moduleId) {
        // start() не сохранил eid/mid (старая сессия или recovery без params).
        // Не делаем тихий no-op — это bug, флагаем для debug.
        console.warn('[sandbox] verify requested but no enrollment/module in session');
        return;
      }
      const { enrollmentId, moduleId } = current;
      // Шаг 1: clear store. fetchWithAuth interceptor в следующем запросе
      // НЕ отправит X-Sandbox-Session — finish-practice пишет в production.
      clearSession('finish');
      setState(readState());

      // Шаг 2: actual finish-practice через service.
      try {
        const result = await learningService.finishPractice(enrollmentId, moduleId);
        // Шаг 3: navigate. AcademyVerifyResultPage прочитает ?passed= из query.
        navigate(`/academy/enrollments/${enrollmentId}/result/${moduleId}?passed=${result.passed}`);
      } catch (err) {
        // 410 уже обработан interceptor'ом (store cleared, expired event).
        // Здесь ловим остальное (502 verify failed, 400 not-started, network).
        const msg = err instanceof Error ? err.message : 'Не удалось проверить практику';
        showToast(msg, 'error');
      }
    };
    window.addEventListener(SANDBOX_VERIFY_EVENT, handleVerify);
    return () => window.removeEventListener(SANDBOX_VERIFY_EVENT, handleVerify);
  }, [navigate]);

  const start = useCallback<SandboxSessionContextValue['start']>((args) => {
    setSession(args);
    // sync immediately — на тот же tick storage был перезаписан, state обновим вручную
    setState(readState());
  }, []);

  const finish = useCallback<SandboxSessionContextValue['finish']>((reason = 'manual') => {
    clearSession(reason);
    setState(readState());
  }, []);

  const isActive = useCallback(
    () => Boolean(state.header) && !!state.expiresAt && !isSandboxExpired(state.expiresAt),
    [state],
  );

  const getRemainingMs = useCallback(
    () => (state.expiresAt ? remainingMs(state.expiresAt) : 0),
    [state],
  );

  const value = useMemo<SandboxSessionContextValue>(
    () => ({
      ...state,
      start,
      finish,
      isActive,
      getRemainingMs,
    }),
    [state, start, finish, isActive, getRemainingMs],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useSandboxSession = (): SandboxSessionContextValue => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSandboxSession must be used within SandboxSessionProvider');
  return v;
};
